import { createContext, useContext } from "react";

// How a stamp opens the capture its time came from. `open` takes the capture's
// event ID and opens that request as a trace. `current` is the capture being
// read: a stamp still names it but does not offer to open it again.
export const StampCaptureContext = createContext(null);

// The one element every trace reading shows a recorded instant with.
//
// A dated stamp is a real `<time>` carrying the machine-readable instant and
// the record it came from, so a reader (and a test) can tell an observed
// response from a recorded message without parsing the label. The undated case
// is deliberately NOT a `<time>`: there is no instant to carry, and emitting
// one with an empty dateTime would make "no record" indistinguishable from a
// record in every query that looks for dated items.
//
// `stamp` is three-state -- see buildTurnStamps. Both missing states render
// nothing; only a recorded instant earns timestamp chrome. A stamp that names
// the capture its time came from opens that capture, so the analyst can check
// a time against the event that supplied it. A button cannot hold another, so
// a render site must not place a stamp inside one.
export function TraceStamp({ stamp, className = "" }) {
  const capture = useContext(StampCaptureContext);
  if (stamp == null) return null;
  const eventId = stamp.eventId || null;
  const opens = Boolean(eventId && capture?.open)
    && eventId.toLowerCase() !== String(capture.current || "").toLowerCase();
  const text = "text-[10px] leading-[1.08] tabular-nums whitespace-nowrap text-[color:var(--muted)]";
  const time = (
    <time
      dateTime={stamp.iso}
      data-timestamp-source={stamp.source}
      {...(stamp.isoEnd ? { "data-timestamp-end": stamp.isoEnd } : {})}
      {...(eventId ? { "data-timestamp-event": eventId } : {})}
      title={opens ? `${stamp.title} Select the time to open that capture.` : stamp.title}
      className={opens ? `${text} hover:text-[color:var(--ink)] hover:underline` : ["shrink-0", text, className].filter(Boolean).join(" ")}
    >
      {stamp.label}
    </time>
  );
  if (!opens) return time;
  return (
    <button
      type="button"
      data-timestamp-open={eventId}
      onClick={(event) => {
        // The stamp sits on cards that open, focus or scrub on click.
        event.stopPropagation();
        capture.open(eventId);
      }}
      className={["shrink-0 cursor-pointer border-0 bg-transparent p-0 leading-none", className].filter(Boolean).join(" ")}
    >
      {time}
    </button>
  );
}
